import React from 'react';
import { useVoice } from '../../contexts/VoiceContext';
import { Microphone, X, WaveformSlash } from '@phosphor-icons/react';

const VoiceInterface = ({ gradeLevel, onExitVoice }) => {
  const {
    voiceState,
    isFadingOut,
    inputTranscript,
    outputTranscript,
    audioLevel,
    voiceError,
    startRecording,
    stopRecording,
    stopVoice,
  } = useVoice();

  const isElementary = gradeLevel === 'elementary';
  const isRecording = voiceState === 'recording';
  const isProcessing = voiceState === 'processing';
  const isSpeaking = voiceState === 'speaking';
  const isReady = voiceState === 'ready';
  const isConnecting = voiceState === 'connecting';

  // Status text
  const getStatusText = () => {
    if (voiceError) return voiceError;
    if (isConnecting) return isElementary ? 'Getting ready...' : 'Connecting...';
    if (isRecording) return isElementary ? 'I\'m listening!' : 'Listening...';
    if (isProcessing) return isElementary ? 'Brillabee is thinking...' : 'Processing...';
    if (isSpeaking) return isElementary ? 'Listen to Brillabee!' : 'Brillabee is talking...';
    if (isFadingOut) return '';
    if (isReady) return isElementary ? 'Hold the button and talk!' : 'Hold to talk';
    return '';
  };

  // Mic button styles
  const getMicButtonClasses = () => {
    const base = `relative rounded-full flex items-center justify-center transition-all select-none touch-none ${
      isElementary ? 'w-20 h-20' : 'w-16 h-16'
    }`;

    if (isRecording) return `${base} bg-red-500 text-white shadow-lg`;
    if (isProcessing) return `${base} bg-indigo-300 text-white cursor-wait`;
    if (isSpeaking) return `${base} bg-indigo-400 text-white animate-pulse-soft`;
    if (isReady) return `${base} bg-indigo-500 text-white hover:bg-indigo-600 cursor-pointer`;
    return `${base} bg-gray-300 text-gray-500 cursor-not-allowed`;
  };

  // Pulse ring scale based on audio level
  const pulseScale = isRecording ? 1 + audioLevel * 0.5 : 1;

  const handlePointerDown = (e) => {
    e.preventDefault();
    if (isReady || isSpeaking) startRecording();
  };

  const handlePointerUp = (e) => {
    e.preventDefault();
    if (isRecording) stopRecording();
  };

  const handleExit = () => {
    stopVoice();
    if (onExitVoice) onExitVoice();
  };

  return (
    <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col items-center gap-4">
      {/* Transcription preview with fade-out transition */}
      <div
        className={`w-full max-w-md min-h-[48px] text-center transition-all duration-400 ${
          isFadingOut ? 'animate-float-up-fade' : ''
        }`}
      >
        {inputTranscript && (
          <p className={`text-gray-600 ${isElementary ? 'text-base' : 'text-sm'} mb-1 transition-opacity duration-300`}>
            <span className="text-gray-400 font-medium">You: </span>
            {inputTranscript}
          </p>
        )}
        {outputTranscript && (
          <p className={`text-indigo-600 ${isElementary ? 'text-base' : 'text-sm'} transition-opacity duration-300`}>
            <span className="text-indigo-400 font-medium">Brillabee: </span>
            {outputTranscript}
          </p>
        )}
      </div>

      {/* Mic button with pulse ring */}
      <div className="relative flex items-center justify-center">
        {/* Recording pulse ring (red) */}
        {isRecording && (
          <div
            className="absolute rounded-full bg-red-400/30 animate-recording-pulse"
            style={{
              width: isElementary ? '96px' : '80px',
              height: isElementary ? '96px' : '80px',
              transform: `scale(${pulseScale})`,
              transition: 'transform 0.1s ease-out',
            }}
          />
        )}

        {/* Speaking glow ring (indigo) */}
        {isSpeaking && (
          <div
            className="absolute rounded-full bg-indigo-400/30 animate-pulse-soft"
            style={{
              width: isElementary ? '96px' : '80px',
              height: isElementary ? '96px' : '80px',
            }}
          />
        )}

        {/* Processing indicator */}
        {isProcessing && (
          <div
            className="absolute rounded-full border-2 border-indigo-300 animate-ping"
            style={{
              width: isElementary ? '88px' : '72px',
              height: isElementary ? '88px' : '72px',
            }}
          />
        )}

        <button
          className={getMicButtonClasses()}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerLeave={isRecording ? handlePointerUp : undefined}
          onContextMenu={(e) => e.preventDefault()}
          disabled={!isReady && !isRecording && !isSpeaking}
          aria-label={isRecording ? 'Release to send' : isSpeaking ? 'Hold to interrupt' : 'Hold to talk'}
        >
          {isRecording ? (
            <Microphone size={isElementary ? 36 : 28} weight="fill" />
          ) : isProcessing ? (
            <WaveformSlash size={isElementary ? 36 : 28} weight="regular" />
          ) : (
            <Microphone size={isElementary ? 36 : 28} weight="regular" />
          )}
        </button>
      </div>

      {/* Status text */}
      <p className={`${isElementary ? 'text-base' : 'text-sm'} ${
        voiceError ? 'text-red-500' : 'text-gray-500'
      } text-center transition-opacity duration-200`}>
        {getStatusText()}
      </p>

      {/* Waveform bars (visible during processing and speaking) */}
      {(isProcessing || isSpeaking) && (
        <div className="flex items-center gap-1 h-6">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="w-1 bg-indigo-400 rounded-full animate-speaking-wave"
              style={{
                height: '16px',
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Exit button */}
      <button
        onClick={handleExit}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors ${
          isElementary ? 'text-base' : 'text-sm'
        }`}
        aria-label="Exit voice mode"
      >
        <X size={isElementary ? 18 : 16} />
        {isElementary ? 'Go back to typing' : 'Back to text'}
      </button>
    </div>
  );
};

export default VoiceInterface;
